import Text from 'components/Text/Text';
import React, { useRef } from 'react';
import { Select } from 'antd';
import SelectDropdownReloader from 'components/SelectDropdownReloader/SelectDropdownReloader';
import { SelectProps, DefaultOptionType } from 'antd/lib/select';
import { ISelectOption } from 'components/Select/types';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import style from './OutsideFormSelect.module.scss';

const { Option } = Select;

type TDefaultSelectProps = Omit<SelectProps, 'options'>;

interface IOutsideFormSelect extends TDefaultSelectProps {
	name: string;
	label: string;
	value: any;
	errorMessage?: string;
	blockId?: string;
	options?: ISelectOption[];
	loading?: boolean;
	fetchError?: boolean;
	placeholder?: string;
	className?: string;
	labelClassName?: string;
	containerClassName?: string;
	withSearch?: boolean;
	keepSearchWithDropdownRender?: boolean;
	counterValue?: number;
	testId?: string;
	withValidation?: boolean;
	withEmptyOption?: boolean;
	onRefetch?: () => void;
	onChange?: (value: any) => void;
}

const OutsideFormSelect: React.FC<IOutsideFormSelect> = ({
	name,
	blockId,
	label,
	value,
	options = [],
	loading = false,
	fetchError,
	placeholder = 'selectPlaceholder',
	className,
	labelClassName,
	containerClassName,
	withSearch = true,
	keepSearchWithDropdownRender = false,
	counterValue,
	errorMessage = 'commonError',
	testId,
	withValidation = true,
	withEmptyOption = true,
	disabled,
	mode,
	dropdownRender,
	onRefetch,
	onChange,
	...restProps
}) => {
	const parentNodeRef = useRef<null | HTMLSpanElement>(null);
	const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
		value,
		errorMessage,
		name,
		blockId,
	);
	const { t } = useTranslation('form');

	const selectContainerClasses = classNames(style.container, containerClassName);
	const selectClasses = classNames(
		style.select,
		{ [style.selectError]: withValidation && isShowValidationError },
		{ [style.withoutInnerItem]: !!counterValue },
		className,
	);
	const labelClasses = classNames(style.label, labelClassName);
	const selectedCounterClasses = classNames(style.selectedCounter, {
		[style.disabledSelectedCounter]: disabled,
	});
	const optionClasses = (disabled: boolean) => classNames(style.listItem, { [style.disabledItem]: disabled });

	const isSearchEnabled = keepSearchWithDropdownRender ? withSearch : !dropdownRender && withSearch;

	const handleRefetch = () => {
		onRefetch?.();
	};

	const handleSearch = (inputValue: string, option: DefaultOptionType | undefined) => {
		if (option?.children && Array.isArray(option.children)) {
			return String(option.children[1]?.props?.translationKey).toLowerCase().includes(inputValue.toLowerCase());
		}
		return false;
	};

	const handleChange = (value: any) => {
		value === undefined ? onChange?.(null) : onChange?.(value); // set null value instead undefined after clearing select
	};

	return (
		<div className={selectContainerClasses} data-test={testId}>
			<Text
				className={labelClasses}
				translationNameSpace="form"
				translationKey={label}
				size="xs"
				theme="neutralLight"
			/>
			{!!counterValue && (
				<Text
					className={selectedCounterClasses}
					translationNameSpace="form"
					translationKey="selectedItemsCounter"
					translationOptions={{ counterValue }}
					size="xs"
				/>
			)}
			<span ref={parentNodeRef} style={{ position: 'relative' }} />
			<Select
				className={selectClasses}
				value={value}
				onChange={handleChange}
				loading={loading}
				showArrow={true}
				allowClear={mode !== 'multiple' && true}
				disabled={disabled}
				showSearch={isSearchEnabled}
				filterOption={isSearchEnabled ? handleSearch : undefined}
				dropdownRender={dropdownRender}
				notFoundContent={fetchError ? <SelectDropdownReloader onReload={handleRefetch} /> : undefined}
				listItemHeight={options.length}
				getPopupContainer={() => parentNodeRef.current as HTMLSpanElement}
				placeholder={!counterValue ? t(placeholder) : undefined}
				mode={mode}
				{...restProps}>
				{options.length ? (
					<>
						{withEmptyOption && (
							<Option
								className={style.disabledItem}
								value="empty"
								disabled={true}
								data-test="value-empty">
								<Text
									translationNameSpace="form"
									translationKey="selectAnyItemPlaceholder"
									asFragment={true}
								/>
							</Option>
						)}
						{options.map((el) => {
							return (
								<Option
									className={optionClasses(!!el.disabled)}
									value={el.id}
									key={el.id}
									disabled={el.disabled}
									data-test={`value-${el.name}`}>
									{el.icon}
									<Text
										className={style.name}
										translationKey={el.name}
										withTooltip={true}
										size="xs"
										weight="medium"
									/>
									{el.additionalContent}
								</Option>
							);
						})}
					</>
				) : null}
			</Select>
			{withValidation && isShowValidationError && (
				<div className={style.errorMessage}>{validationErrorMessage}</div>
			)}
		</div>
	);
};

export default React.memo(OutsideFormSelect);
